import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { getCmRoi, saveCmRoi } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { RefreshCw, ShieldCheck, ServerCog, CloudCog, Sparkles } from "lucide-react";

const BASELINE_FIELDS = [
  ["annual_infrastructure_cost", "Annual Infrastructure Cost"],
  ["annual_hosting_cost", "Annual Hosting / Data Centre Cost"],
  ["annual_hardware_maintenance_cost", "Annual Hardware Maintenance Cost"],
  ["annual_basis_support_cost", "Annual Basis Support Cost"],
  ["annual_platform_admin_cost", "Annual Platform Administration Cost"],
  ["annual_backup_dr_cost", "Annual Backup and Disaster Recovery Cost"],
  ["annual_security_compliance_cost", "Annual Security and Compliance Cost"],
  ["avg_cost_per_downtime_hour", "Average Cost per Downtime Hour"],
  ["expected_annual_downtime_hours", "Expected Annual Downtime Hours"],
  ["future_cloud_subscription_cost", "Future Cloud Subscription Cost"],
  ["future_managed_service_cost", "Future Managed Service Cost"],
  ["future_cloud_operations_cost", "Future Cloud Operations Cost"],
  ["annual_innovation_avoidance_baseline", "Annual Future Upgrade / Innovation Avoidance Baseline"],
];

const STRATEGIC_BENEFITS = [
  ["Improved platform supportability", ServerCog],
  ["Better disaster recovery readiness", ShieldCheck],
  ["Improved security and compliance posture", ShieldCheck],
  ["Readiness for future SAP innovation", Sparkles],
];

export default function CmRoiTab({ assessmentId }) {
  const [data, setData] = useState(null);
  const [baseline, setBaseline] = useState({});
  const [scenario, setScenario] = useState("medium");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("USD");
  const [exchangeRate, setExchangeRate] = useState(1);

  const applyResponse = (d) => {
    setData(d);
    setBaseline(d.inputs?.baseline || {});
    setScenario(d.inputs?.scenario || "medium");
    setCurrency(d.inputs?.currency || "USD");
    setExchangeRate(d.inputs?.exchange_rate || 1);
  };

  const load = useCallback(() => {
    setLoading(true);
    getCmRoi(assessmentId)
      .then(applyResponse)
      .catch(() => toast.error("Failed to load ROI"))
      .finally(() => setLoading(false));
  }, [assessmentId]);
  useEffect(() => { load(); }, [load]);

  // Recalculate always sends the CURRENT form values; `overrides` lets a control
  // (e.g. scenario/currency button) pass its new value without waiting for a
  // React state flush, so the calculation never uses stale state.
  const recalc = async (overrides = {}) => {
    setSaving(true);
    const payload = {
      baseline: overrides.baseline ?? baseline,
      scenario: overrides.scenario ?? scenario,
      currency: overrides.currency ?? currency,
      exchange_rate: overrides.exchange_rate ?? exchangeRate,
    };
    try {
      const d = await saveCmRoi(assessmentId, payload);
      applyResponse(d);
      toast.success("ROI recalculated and saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save ROI");
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-sm text-[#6B7280]" data-testid="cm-roi-loading">Loading ROI…</p>;
  if (!data) return null;

  const ctx = data.context || {};
  const fmt = (v) => v == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(v * exchangeRate);

  if (!data.calculable) {
    return (
      <div data-testid="cm-roi-not-configured" className="bg-white border border-[#FDBA74]/50 rounded-sm p-8 text-center">
        <CloudCog className="w-8 h-8 text-[#9A3412] mx-auto mb-3" />
        <p className="text-sm font-semibold text-[#9A3412]">{data.message || "ROI model is not yet configured."}</p>
        <p className="text-xs text-[#6B7280] mt-2">Transformation Strategy: <strong>{ctx.transformation_strategy_label}</strong></p>
      </div>
    );
  }

  const o = data.outputs || {};
  const set = (k, v) => setBaseline((b) => ({ ...b, [k]: v === "" ? null : parseFloat(v) }));

  // Dynamic baseline inputs — driven by the ROI model (infra always; operational
  // fields only for Selective Migration / Full Transformation). Falls back to
  // the legacy infrastructure-only list for older payloads.
  const fieldDefs = (data.baseline_field_defs && data.baseline_field_defs.length)
    ? data.baseline_field_defs
    : BASELINE_FIELDS.map(([k, label]) => ({ key: k, label, group: "infrastructure", group_label: "Infrastructure & Platform Baseline" }));
  const groupedFields = fieldDefs.reduce((acc, f) => {
    const gl = f.group_label || "Baseline";
    (acc[gl] = acc[gl] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="cm-roi-tab">
      {/* A. ROI Context */}
      <section className="bg-gradient-to-r from-[#025979] to-[#024a63] text-white rounded-md p-4" data-testid="cm-roi-context">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {[
            ["Transformation Strategy", ctx.transformation_strategy_label],
            ["Implementation Approach", ctx.implementation_approach_label],
            ["ROI Model", ctx.model_name],
            ["Evaluation Period", `${ctx.evaluation_period_years} Years`],
          ].map(([l, v]) => (
            <div key={l}>
              <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold">{l}</p>
              <p className="text-sm font-bold mt-0.5">{v || "—"}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-4 border-t border-white/15 pt-3">
          <div>
            <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold mb-1">Benefit Scenario</p>
            <div className="flex gap-1">
              {["low", "medium", "high"].map((s) => (
                <button key={s} data-testid={`cm-roi-scenario-${s}`} onClick={() => recalc({ scenario: s })} disabled={saving}
                  className={`px-2.5 py-1 text-xs rounded-sm border capitalize ${scenario === s ? "border-white bg-white text-[#025979] font-semibold" : "border-white/30 text-white/80 hover:border-white"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold mb-1">Display Currency</p>
            <select data-testid="cm-roi-currency" value={currency}
              onChange={(e) => { const c = e.target.value; setCurrency(c); recalc({ currency: c }); }} disabled={saving}
              className="px-2 py-1 text-xs rounded-sm text-[#111827] focus:outline-none">
              {["USD", "EUR", "GBP", "INR", "AED"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold mb-1">Exchange Rate (1 USD →)</p>
            <input type="number" step="0.0001" data-testid="cm-roi-exchange-rate" value={exchangeRate}
              onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 0)}
              onBlur={() => recalc({})} disabled={saving}
              className="px-2 py-1 text-xs rounded-sm text-[#111827] w-24 focus:outline-none" />
          </div>
        </div>
      </section>

      {/* B. Executive ROI Summary */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cm-roi-summary">
        {[
          ["Total Investment", fmt(o.total_investment), "cm-roi-investment"],
          ["Net Annual Benefit", fmt(o.net_annual_benefit), "cm-roi-net-annual"],
          ["Payback", o.no_payback ? "No Payback in Period" : `${o.payback_months} mo`, "cm-roi-payback"],
          ["5-Year ROI", `${o.five_year_roi_percent}%`, "cm-roi-5yr-roi"],
          ["5-Year NPV", fmt(o.five_year_npv), "cm-roi-5yr-npv"],
        ].map(([l, v, tid]) => (
          <div key={l} data-testid={tid} className="bg-white border border-[#E5E7EB] rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{l}</p>
            <p className="text-base font-bold text-[#111827] mt-1">{v}</p>
          </div>
        ))}
      </section>

      {/* C. Benefit Breakdown */}
      <section className="bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid="cm-roi-benefit-breakdown">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Benefit Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr><th className="px-3 py-2">Benefit Category</th><th className="px-3 py-2 text-right">Baseline</th><th className="px-3 py-2 text-right">Applied %</th><th className="px-3 py-2 text-right">Gross</th><th className="px-3 py-2 text-right">Risk-Adjusted</th></tr>
            </thead>
            <tbody>
              {(o.benefit_breakdown || []).map((b) => (
                <tr key={b.key} className="border-t border-[#F1F5F9]">
                  <td className="px-3 py-2 font-medium text-[#111827]">{b.name}{b.strategic_only && <span className="ml-1 text-[9px] text-[#9A3412] uppercase">strategic</span>}</td>
                  <td className="px-3 py-2 text-right text-[#6B7280]">{fmt(b.baseline)}</td>
                  <td className="px-3 py-2 text-right">{b.applied_pct}%</td>
                  <td className="px-3 py-2 text-right">{fmt(b.gross_benefit)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmt(b.risk_adjusted_benefit)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#E5E7EB] font-bold">
                <td className="px-3 py-2">Total</td><td /><td />
                <td className="px-3 py-2 text-right">{fmt(o.gross_annual_benefit)}</td>
                <td className="px-3 py-2 text-right">{fmt(o.risk_adjusted_annual_benefit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* D. Current vs Future Run Cost */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cm-roi-run-cost">
        {[
          ["Current Annual Run Cost", o.current_annual_run_cost],
          ["Future Annual Run Cost", o.future_annual_run_cost],
          ["Net Annual Run-Cost Change", o.net_annual_run_change],
        ].map(([l, v]) => (
          <div key={l} className="bg-white border border-[#E5E7EB] rounded-sm p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{l}</p>
            <p className={`text-base font-bold mt-1 ${l.includes("Change") ? (v >= 0 ? "text-[#065F46]" : "text-[#B91C1C]") : "text-[#111827]"}`}>{fmt(v)}</p>
          </div>
        ))}
      </section>

      {/* E. Current-State Cost Baseline (editable) + scenario */}
      <section className="bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid="cm-roi-baseline">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold" style={{ fontFamily: "Chivo" }}>Current-State Baseline</h3>
          <span className="text-[10px] text-[#9CA3AF]">Scenario &amp; currency are set in the ROI context above.</span>
        </div>
        {Object.entries(groupedFields).map(([groupLabel, fields]) => (
          <div key={groupLabel} className="mb-5 last:mb-0" data-testid={`cm-roi-baseline-group-${groupLabel.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="text-[10px] uppercase tracking-wider text-[#025979] font-semibold mb-2">{groupLabel}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="text-xs font-medium text-[#374151] mb-1 block">{f.label}</label>
                  <Input type="number" data-testid={`cm-roi-input-${f.key}`} value={baseline[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className="rounded-sm text-xs" />
                </div>
              ))}
            </div>
          </div>
        ))}
        <p className="text-[10px] text-[#9CA3AF] mt-3 italic">These values are saved against this assessment only and never modify the Admin ROI model.</p>
        <div className="mt-4 flex justify-end">
          <Button data-testid="cm-roi-recalc-btn" onClick={recalc} disabled={saving} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 mr-1 ${saving ? "animate-spin" : ""}`} /> {saving ? "Saving…" : "Recalculate ROI"}
          </Button>
        </div>
      </section>

      {/* F. Investment Summary */}
      <section className="bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid="cm-roi-investment-summary">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2" style={{ fontFamily: "Chivo" }}>Investment Summary</h3>
        <div className="flex items-baseline justify-between">
          <p className="text-lg font-bold text-[#111827]">{fmt(o.total_investment)}</p>
          <span className="text-[10px] text-[#025979] bg-[#E0F2FE] border border-[#025979]/20 rounded-full px-2 py-0.5">{o.investment_source}</span>
        </div>
        <p className="text-[10px] text-[#9CA3AF] mt-2">Approach modifiers ({ctx.implementation_approach_label}): effort ×{o.approach_modifiers?.implementation_effort_multiplier}, testing ×{o.approach_modifiers?.testing_validation_multiplier}, cutover ×{o.approach_modifiers?.cutover_prep_multiplier}, risk buffer {o.approach_modifiers?.risk_buffer_pct}%, {o.approach_modifiers?.indicative_duration}. Applied only when deriving missing estimates — the Implementation plan total is used as-is to avoid double-counting.</p>
      </section>

      {/* G. Sensitivity */}
      <section className="grid grid-cols-3 gap-3" data-testid="cm-roi-sensitivity">
        {[["Low", o.sensitivity?.low], ["Base", o.sensitivity?.base], ["High", o.sensitivity?.high]].map(([l, v]) => (
          <div key={l} className="bg-white border border-[#E5E7EB] rounded-sm p-3 text-center">
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{l} — Net Annual Benefit</p>
            <p className="text-sm font-bold text-[#111827] mt-1">{fmt(v)}</p>
          </div>
        ))}
      </section>

      {/* H. Strategic Benefits */}
      <section className="bg-white border border-[#E5E7EB] rounded-sm p-5" data-testid="cm-roi-strategic">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold mb-3" style={{ fontFamily: "Chivo" }}>Strategic Benefits (non-financial)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {STRATEGIC_BENEFITS.map(([label, Icon]) => (
            <div key={label} className="flex items-center gap-2 text-xs text-[#374151]">
              <Icon className="w-4 h-4 text-[#025979]" /> {label}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
